/**
 * ContactPEMTab — the Patient Experience workflow surface inside the
 * CRM contact detail page.
 *
 * Lets the PEM advance the patient through the journey lifecycle without
 * leaving the contact view. Operationalizes the backend shipped in
 * migrations 098 + 099 and routes/pem.js + routes/pem_concierge.js.
 *
 * Sections:
 *   - Journey state widget — current stage badge + legal-next-stage
 *     transition buttons + stage history timeline + SLA indicator
 *   - Concierge messaging — template picker, preview, send (audit-logged)
 *   - Follow-up tasks — auto-generated 24h/7d/30d/90d/6mo/12mo touchpoints
 *     visible for this contact, one-click complete
 *   - Review requests — per-contact review-request log + outcome capture
 *   - Family support contact — encrypted name + phone (PHI columns
 *     registered in encryptedColumns.js)
 *   - Referral attribution — referred-by picker + downstream referrals
 *
 * Permission: pem:read to view, pem:write to mutate.
 */
import React, { useState, useEffect, useCallback } from "react";
import {
  Activity, ArrowRight, AlertCircle, CheckCircle2, Clock, Loader2,
  Send, Sparkles, Star, Users, Phone, Mail, RefreshCcw, ChevronRight,
} from "lucide-react";

// ─── Stage display helpers ──────────────────────────────────────────────
const STAGE_LABEL = {
  new_lead: "New Lead", qualified: "Qualified",
  consult_scheduled: "Consult Scheduled", consult_completed: "Consult Completed",
  deposit_paid: "Deposit Paid", treatment_scheduled: "Treatment Scheduled",
  treatment_in_progress: "Treatment in Progress", treatment_completed: "Treatment Completed",
  follow_up_24h: "24-Hour Follow-up", follow_up_7d: "7-Day Follow-up",
  follow_up_30d: "30-Day Follow-up", follow_up_90d: "90-Day Follow-up",
  follow_up_6mo: "6-Month Follow-up", follow_up_12mo: "12-Month Follow-up",
  wellness_continuity: "Wellness Continuity",
  lost: "Lost", completed: "Completed",
};

const OUTCOME_OPTIONS = [
  { value: "contacted_well",              label: "Patient doing well" },
  { value: "contacted_concern",           label: "Concern flagged" },
  { value: "no_answer",                   label: "No answer / left message" },
  { value: "reschedule_requested",        label: "Reschedule requested" },
  { value: "wellness_continuity_offered", label: "Wellness continuity offered" },
  { value: "review_requested",            label: "Review request sent" },
  { value: "referral_received",           label: "Referral received" },
];

export default function ContactPEMTab({ contactId, contactData, canUpdate, onReload }) {
  // Journey state
  const [journey, setJourney] = useState(null);
  const [stagesRef, setStagesRef] = useState(null);
  const [followUps, setFollowUps] = useState([]);
  const [conciergeHistory, setConciergeHistory] = useState([]);
  const [reviewRequests, setReviewRequests] = useState([]);
  const [templates, setTemplates] = useState([]);

  const [loading, setLoading] = useState(true);
  // See the render gate below: distinguishes a FIRST load from a refresh.
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [success, setSuccess] = useState(null);

  // Composer state
  const [sendingTemplateKey, setSendingTemplateKey] = useState(null);
  const [previewState, setPreviewState] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const api = window.zignagenixAPI;
      const [j, stages, fups, hist, revs, tpls] = await Promise.all([
        api.invoke("pem:journey:get", contactId).catch(() => null),
        api.invoke("pem:stages:reference").catch(() => null),
        api.invoke("pem:follow-ups:list-due", { horizonDays: 90, mineOnly: false }).catch(() => []),
        api.invoke("concierge:history", { contactId, limit: 30 }).catch(() => []),
        api.invoke("review-requests:by-contact", contactId).catch(() => []),
        api.invoke("concierge:templates:list").catch(() => []),
      ]);
      setJourney(j && !j.error ? j : null);
      setStagesRef(stages);
      // Filter follow-ups list down to this contact
      setFollowUps(Array.isArray(fups) ? fups.filter((f) => f.contact_id === contactId) : []);
      setConciergeHistory(Array.isArray(hist) ? hist : []);
      setReviewRequests(Array.isArray(revs) ? revs : []);
      setTemplates(Array.isArray(tpls) ? tpls : []);
    } catch (e) {
      setError((e && e.message) || "Failed to load");
    }
    setLoading(false);
    setHasLoaded(true);
  }, [contactId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  async function ensureJourney() {
    setBusy("ensure");
    try {
      const r = await window.zignagenixAPI.invoke("pem:journey:ensure", { contactId });
      if (r && r.error) setError(r.message || r.code);
      else { setSuccess("Journey row initialized at " + (r.current_stage || "new_lead")); await loadAll(); }
    } catch (e) { setError((e && e.message) || "Failed"); }
    setBusy(null);
  }

  async function transitionTo(toStage) {
    setBusy(`transition-${toStage}`);
    setSuccess(null);
    setError(null);
    try {
      const notes = window.prompt(`Add a note about moving to ${STAGE_LABEL[toStage] || toStage}? (optional)`) || "";
      const r = await window.zignagenixAPI.invoke("pem:journey:transition", {
        contactId, toStage, notes: notes.trim(),
      });
      if (r && r.error) {
        setError(r.message || r.code);
      } else {
        setSuccess(
          `Moved to ${STAGE_LABEL[toStage] || toStage}` +
          (r.follow_ups_created ? ` (${r.follow_ups_created} follow-up touchpoints auto-generated)` : "")
        );
        await loadAll();
      }
    } catch (e) { setError((e && e.message) || "Transition failed"); }
    setBusy(null);
  }

  async function previewTemplate(templateKey) {
    setSendingTemplateKey(templateKey);
    setPreviewState({ loading: true });
    try {
      const r = await window.zignagenixAPI.invoke("concierge:preview", {
        templateKey, contactId,
      });
      if (r && r.error) {
        setPreviewState({ error: r.message || r.code });
      } else {
        setPreviewState(r);
      }
    } catch (e) {
      setPreviewState({ error: (e && e.message) || "Preview failed" });
    }
  }

  async function sendMessage(channel) {
    if (!previewState || !sendingTemplateKey) return;
    setBusy("send");
    setError(null);
    try {
      const r = await window.zignagenixAPI.invoke("concierge:send", {
        templateKey: sendingTemplateKey, contactId, channel,
      });
      if (r && r.error) {
        if (r.code === "UNRESOLVED_VARIABLES") {
          setError(`Cannot send — template has unresolved variables: ${r.missing.join(", ")}. Edit the contact record to fill these in.`);
        } else {
          setError(r.message || r.code);
        }
      } else {
        setSuccess(`Message queued (${channel} → ${r.recipient}). Audit row written.`);
        setSendingTemplateKey(null);
        setPreviewState(null);
        await loadAll();
      }
    } catch (e) { setError((e && e.message) || "Send failed"); }
    setBusy(null);
  }

  async function completeFollowUp(id, outcome) {
    setBusy(`fup-${id}`);
    try {
      const r = await window.zignagenixAPI.invoke("pem:follow-ups:complete", {
        id, outcome, notes: "",
      });
      if (r && r.error) setError(r.message || r.code);
      else { setSuccess("Follow-up completed."); await loadAll(); }
    } catch (e) { setError((e && e.message) || "Failed"); }
    setBusy(null);
  }

  async function createReviewRequest(platform) {
    setBusy(`review-${platform}`);
    try {
      const r = await window.zignagenixAPI.invoke("review-requests:create", {
        contactId, platform,
      });
      if (r && r.error) setError(r.message || r.code);
      else { setSuccess(`${platform} review request logged.`); await loadAll(); }
    } catch (e) { setError((e && e.message) || "Failed"); }
    setBusy(null);
  }

  /*
    ⛔⛔ `loading && !hasLoaded`, NOT `loading` — Card CRM-SCROLL-JUMP review
    round 1 (P64a), 9 Oct 2026. Same defect, same shape, same page as the one
    this card was opened for.

    Every action on this tab calls the loader when it succeeds, and the loader
    sets loading=true. A bare `if (loading)` therefore replaced the ENTIRE tab
    with a centred spinner on every save — the tree unmounted, the scroll
    container's content vanished and its scrollTop was clamped to 0, and the
    staff member was returned to the top of the form mid-task.

    ⚠ A FIRST load still gets the spinner, because there is genuinely nothing to
    show. A REFRESH leaves the existing content on screen and updates underneath
    it, which is what an autosave should look like.

    ⚠ An explicit flag rather than "is some payload non-null": on this tab a null
    payload is a REAL state (no journey row yet, no candidacy recorded), so
    keying the gate on one would blank the tab for exactly the contacts that have
    not been set up — precisely the ones staff are working on.
  */
  if (loading && !hasLoaded) {
    return <div style={{ padding: 24, textAlign: "center", color: "#9aa4b2" }}><Loader2 className="spin" /> Loading PEM workflow…</div>;
  }

  // ─── Render ────────────────────────────────────────────────────────
  const legalNextStages = journey && stagesRef && stagesRef.transitions
    ? (stagesRef.transitions[journey.current_stage] || [])
    : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {/* ── Status banner ─────────────────────────────────────── */}
      {error && (
        <div style={alertStyle("error")}><AlertCircle size={14} /> {error}</div>
      )}
      {success && (
        <div style={alertStyle("ok")}><CheckCircle2 size={14} /> {success}</div>
      )}

      {/* ── Journey state widget ──────────────────────────────── */}
      <div style={panel}>
        <div style={panelHeader}>
          <Activity size={14} color="#1f6feb" />
          <strong>Patient Journey</strong>
          {journey && (
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#5a6478" }}>
              <RefreshCcw size={10} style={{ verticalAlign: "middle" }} /> {new Date(journey.updated_at).toLocaleString()}
            </span>
          )}
        </div>

        {!journey ? (
          <div style={{ padding: 12, color: "#9aa4b2" }}>
            <p style={{ fontSize: 13, margin: 0, marginBottom: 8 }}>
              This contact has no journey row yet. Initialize it to start tracking through the 15-stage lifecycle.
            </p>
            {canUpdate && (
              <button onClick={ensureJourney} disabled={busy === "ensure"} style={btnPrimary}>
                {busy === "ensure" ? <Loader2 size={12} className="spin" /> : <Sparkles size={12} />}
                Initialize at New Lead
              </button>
            )}
          </div>
        ) : (
          <>
            <div style={{ padding: 12, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={stageBadge(journey.current_stage)}>
                {STAGE_LABEL[journey.current_stage] || journey.current_stage}
              </span>
              {journey.sla_breach_at && new Date(journey.sla_breach_at) < new Date() && (
                <span style={{ fontSize: 11, color: "#b00020", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 3 }}>
                  <AlertCircle size={11} /> SLA BREACHED
                </span>
              )}
              {journey.sla_breach_at && new Date(journey.sla_breach_at) >= new Date() && (
                <span style={{ fontSize: 11, color: "#5a6478" }}>
                  <Clock size={10} style={{ verticalAlign: "middle" }} /> SLA: {new Date(journey.sla_breach_at).toLocaleString()}
                </span>
              )}
              <span style={{ fontSize: 11, color: "#5a6478", marginLeft: "auto" }}>
                Entered {new Date(journey.entered_stage_at).toLocaleDateString()}
              </span>
            </div>

            {/* Legal-next-stage transition buttons */}
            {canUpdate && legalNextStages.length > 0 && (
              <div style={{ padding: "0 12px 12px", display: "flex", flexWrap: "wrap", gap: 6 }}>
                <span style={{ fontSize: 11, color: "#5a6478", marginRight: 4 }}>Move to:</span>
                {legalNextStages.map((s) => (
                  <button
                    key={s}
                    onClick={() => transitionTo(s)}
                    disabled={busy === `transition-${s}`}
                    style={s === "lost" ? btnGhostDanger : btnGhost}
                  >
                    {busy === `transition-${s}` ? <Loader2 size={11} className="spin" /> : <ArrowRight size={11} />}
                    {STAGE_LABEL[s] || s}
                  </button>
                ))}
              </div>
            )}

            {/* Stage history timeline */}
            {Array.isArray(journey.stage_history) && journey.stage_history.length > 0 && (
              <details style={{ padding: "0 12px 12px", fontSize: 11, color: "#5a6478" }}>
                <summary style={{ cursor: "pointer" }}>Stage history ({journey.stage_history.length})</summary>
                <div style={{ marginTop: 6 }}>
                  {journey.stage_history.slice().reverse().map((h, i) => (
                    <div key={i} style={{ padding: "3px 0", borderBottom: "1px solid #f0f3f7" }}>
                      <ChevronRight size={9} style={{ verticalAlign: "middle" }} />
                      {STAGE_LABEL[h.from] || h.from} → {STAGE_LABEL[h.to] || h.to}
                      <span style={{ color: "#9aa4b2", marginLeft: 6 }}>
                        {new Date(h.at).toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </>
        )}
      </div>

      {/* ── Concierge messaging ─────────────────────────────── */}
      <div style={panel}>
        <div style={panelHeader}>
          <Send size={14} color="#6b1f8b" />
          <strong>Concierge Messaging</strong>
          <span style={{ marginLeft: "auto", fontSize: 11, color: "#5a6478" }}>
            {conciergeHistory.length} sent
          </span>
        </div>
        <div style={{ padding: 12 }}>
          {/* Template picker */}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
            {templates.map((t) => (
              <button
                key={t.template_key}
                onClick={() => previewTemplate(t.template_key)}
                disabled={!canUpdate}
                style={templateButton}
                title={t.description}
              >
                <Sparkles size={10} /> {t.name}
              </button>
            ))}
          </div>

          {/* Preview + send */}
          {previewState && (
            <div style={{ background: "#f4f7fb", border: "1px solid #c9d6e8", borderRadius: 4, padding: 10, marginBottom: 10 }}>
              {previewState.loading ? (
                <div><Loader2 className="spin" size={12} /> Generating preview…</div>
              ) : previewState.error ? (
                <div style={{ color: "#b00020", fontSize: 12 }}>{previewState.error}</div>
              ) : (
                <>
                  {previewState.rendered_subject && (
                    <div style={{ fontSize: 11, color: "#5a6478", marginBottom: 4 }}>
                      <strong>Subject:</strong> {previewState.rendered_subject}
                    </div>
                  )}
                  <div style={{ fontSize: 12, whiteSpace: "pre-wrap", background: "#fff", padding: 8, borderRadius: 3, border: "1px solid #e1e6ed", maxHeight: 200, overflowY: "auto" }}>
                    {previewState.rendered_body}
                  </div>
                  {previewState.missing_variables && previewState.missing_variables.length > 0 && (
                    <div style={{ fontSize: 11, color: "#7c5210", marginTop: 6 }}>
                      ⚠ Missing variables: {previewState.missing_variables.join(", ")} — fill in the contact record before sending.
                    </div>
                  )}
                  <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                    {(previewState.channel === "email" || previewState.channel === "both") && previewState.recipient_email && (
                      <button onClick={() => sendMessage("email")} disabled={busy === "send"} style={btnPrimary}>
                        <Mail size={11} /> Send Email to {previewState.recipient_email}
                      </button>
                    )}
                    {(previewState.channel === "sms" || previewState.channel === "both") && previewState.recipient_phone && (
                      <button onClick={() => sendMessage("sms")} disabled={busy === "send"} style={btnPrimary}>
                        <Phone size={11} /> Send SMS to {previewState.recipient_phone}
                      </button>
                    )}
                    <button onClick={() => { setPreviewState(null); setSendingTemplateKey(null); }} style={btnGhost}>
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* History */}
          {conciergeHistory.length > 0 && (
            <details>
              <summary style={{ cursor: "pointer", fontSize: 11, color: "#5a6478" }}>
                Recent concierge messages ({conciergeHistory.length})
              </summary>
              <div style={{ marginTop: 6 }}>
                {conciergeHistory.slice(0, 8).map((h) => (
                  <div key={h.id} style={{ padding: "6px 0", borderBottom: "1px solid #f0f3f7", fontSize: 11 }}>
                    <strong>{h.template_key}</strong> via {h.channel} · {new Date(h.sent_at).toLocaleString()} by {h.sent_by_name || h.sent_by_user_id}
                  </div>
                ))}
              </div>
            </details>
          )}
        </div>
      </div>

      {/* ── Follow-ups due ──────────────────────────────────── */}
      <div style={panel}>
        <div style={panelHeader}>
          <Clock size={14} color="#7c5210" />
          <strong>Follow-up Touchpoints</strong>
          <span style={{ marginLeft: "auto", fontSize: 11, color: "#5a6478" }}>
            {followUps.length} due
          </span>
        </div>
        {followUps.length === 0 ? (
          <div style={{ padding: 12, color: "#9aa4b2", fontSize: 12, fontStyle: "italic" }}>
            No follow-up touchpoints due. Auto-generated 24h/7d/30d/90d/6mo/12mo touchpoints appear here after the patient enters Treatment Completed.
          </div>
        ) : (
          <div>
            {followUps.map((f) => {
              const due = new Date(f.due_at);
              const overdue = due.getTime() < Date.now();
              return (
                <div key={f.id} style={{
                  padding: "10px 12px", borderTop: "1px solid #f0f3f7",
                  background: overdue ? "#fff7f7" : "transparent",
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={cadenceBadge(overdue)}>{f.cadence_marker}</span>
                    {overdue && <span style={{ fontSize: 10, color: "#b00020", fontWeight: 600 }}>OVERDUE</span>}
                    <span style={{ fontSize: 11, color: "#5a6478", marginLeft: "auto" }}>
                      Due {due.toLocaleString()}
                    </span>
                  </div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                    {OUTCOME_OPTIONS.map((o) => (
                      <button
                        key={o.value}
                        onClick={() => completeFollowUp(f.id, o.value)}
                        disabled={busy === `fup-${f.id}`}
                        style={outcomeButton}
                      >{o.label}</button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Review requests ─────────────────────────────────── */}
      <div style={panel}>
        <div style={panelHeader}>
          <Star size={14} color="#e0b973" />
          <strong>Review Requests</strong>
          <span style={{ marginLeft: "auto", fontSize: 11, color: "#5a6478" }}>
            {reviewRequests.length} requested
          </span>
        </div>
        <div style={{ padding: 12 }}>
          {canUpdate && (
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              <button onClick={() => createReviewRequest("google")} disabled={busy === "review-google"} style={btnGhost}>
                Request Google review
              </button>
              <button onClick={() => createReviewRequest("trustpilot")} disabled={busy === "review-trustpilot"} style={btnGhost}>
                Request Trustpilot review
              </button>
            </div>
          )}
          {reviewRequests.length === 0 ? (
            <div style={{ color: "#9aa4b2", fontSize: 12, fontStyle: "italic" }}>
              No review requests yet. Request reviews after positive follow-ups for the Patient Experience KPI.
            </div>
          ) : (
            reviewRequests.map((r) => (
              <div key={r.id} style={{ padding: "6px 0", borderBottom: "1px solid #f0f3f7", fontSize: 11 }}>
                <strong>{r.platform}</strong> · {new Date(r.requested_at).toLocaleDateString()} ·
                <span style={{ marginLeft: 6, color: r.outcome === "review_left" ? "#2d6b2d" : "#5a6478" }}>
                  {r.outcome}
                </span>
                {r.star_rating && <span style={{ marginLeft: 6, color: "#e0b973" }}>{"★".repeat(r.star_rating)}</span>}
              </div>
            ))
          )}
        </div>
      </div>

      {/* ── Audit footer ───────────────────────────────────── */}
      <div style={{ fontSize: 10, color: "#9aa4b2", padding: "4px 8px", fontStyle: "italic" }}>
        Every transition, follow-up, message send, and review request is audit-logged per HIPAA §164.312(b).
        Concierge templates resolve {"{{variables}}"} server-side and reject sends with unresolved values to prevent partial-template emails.
      </div>
    </div>
  );
}

// ─── Helpers + styles ──────────────────────────────────────────────────
function stageBadge(stage) {
  const c = stage === "lost" ? "#b00020"
    : stage === "completed" || stage === "wellness_continuity" ? "#2d6b2d"
    : stage && stage.startsWith("follow_up") ? "#1f6feb"
    : stage && stage.startsWith("treatment") ? "#6b1f8b"
    : "#7c5210";
  return {
    display: "inline-block",
    padding: "4px 12px", borderRadius: 12,
    background: c, color: "#fff",
    fontSize: 12, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: 0.5,
  };
}
function cadenceBadge(overdue) {
  return {
    fontSize: 10, fontWeight: 700, padding: "2px 6px", borderRadius: 3,
    background: overdue ? "#b00020" : "#1f6feb", color: "#fff",
    textTransform: "uppercase", letterSpacing: 0.3,
  };
}
const panel = { background: "#fff", border: "1px solid #e1e6ed", borderRadius: 6 };
const panelHeader = {
  padding: "10px 12px", borderBottom: "1px solid #f0f3f7",
  display: "flex", alignItems: "center", gap: 8, fontSize: 13,
};
const btnPrimary = {
  padding: "5px 10px", fontSize: 12, fontWeight: 600,
  background: "#1f6feb", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 4,
};
const btnGhost = {
  padding: "5px 10px", fontSize: 11, fontWeight: 600,
  background: "#fff", color: "#3a4a5e", border: "1px solid #c9d6e8", borderRadius: 3, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 4,
};
const btnGhostDanger = {
  ...btnGhost, color: "#b00020", borderColor: "#f5a5a5",
};
const templateButton = {
  padding: "4px 10px", fontSize: 11, fontWeight: 600,
  background: "#fff", color: "#6b1f8b", border: "1px solid #d6c9e8", borderRadius: 12, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 4,
};
const outcomeButton = {
  padding: "3px 8px", fontSize: 10, fontWeight: 600,
  background: "#fff", color: "#1f6feb", border: "1px solid #c9d6e8", borderRadius: 3, cursor: "pointer",
};
function alertStyle(kind) {
  return {
    padding: "8px 12px", borderRadius: 4, fontSize: 12,
    display: "flex", alignItems: "center", gap: 6,
    background: kind === "ok" ? "#e9f5e9" : "#fde8e8",
    border: kind === "ok" ? "1px solid #a5d5a5" : "1px solid #f5a5a5",
    color: kind === "ok" ? "#2d6b2d" : "#8b0000",
  };
}
